import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconSearch from '@/assets/icons/icon-search.svg?react';
import IconFilter from '@/assets/icons/icon-filter-horizontal.svg?react';
import IconFilterFilled from '@/assets/icons/icon-filter-horizontal-filled.svg?react';
import { Radio } from '@/components/forms/Radio/Radio';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { SECTION_CATEGORIES, normalizeSectionGroupId } from '@builder4/shared/utils/constants';
import type { Section } from '@/store/section/types';

interface SectionFilterItem {
  id: string;
  label: string;
  count: number;
}

export interface SaveSectionFiltersProps {
  className?: string;
  onApply?: (activeFilterIds: string[]) => void;
  onClose?: () => void;
  filters?: SectionFilterItem[];
  allLabel?: string;
  defaultOpen?: boolean;
  search?: string;
  onSearchChange?: (val: string) => void;
  savedSections?: Section[];
}

export const SaveSectionFilters: React.FC<SaveSectionFiltersProps> = ({
  className = '',
  onApply,
  onClose,
  filters,
  allLabel = 'All sections',
  defaultOpen = true,
  search,
  onSearchChange,
  savedSections = [],
}) => {
  const [internalSearch, setInternalSearch] = useState('');
  const [open, setOpen] = useState(defaultOpen);
  const [activeId, setActiveId] = useState<string>(''); // '' represents ALL

  // Controlled/uncontrolled search handling: prefer prop if provided
  const searchValue = typeof search === 'string' ? search : internalSearch;
  const setSearchValue = (val: string) => {
    if (typeof onSearchChange === 'function') onSearchChange(val);
    else setInternalSearch(val);
  };

  // Calculate real counts from savedSections
  const calculatedFilters = useMemo(() => {
    if (filters) return filters; // Use provided filters if available

    return Object.entries(SECTION_CATEGORIES).map(([key, category]) => {
      const count = savedSections.filter(
        (section) => normalizeSectionGroupId(section.sectionGroupId) === key,
      ).length;
      return {
        id: key,
        label: category.label,
        count,
      };
    });
  }, [filters, savedSections]);

  const allCount = useMemo(
    () => calculatedFilters.reduce((totalCount, filterItem) => totalCount + filterItem.count, 0),
    [calculatedFilters],
  );
  const isAllActive = activeId === '';
  const radioValue = isAllActive ? 'ALL' : activeId;
  const selectFilter = useCallback((id: string) => setActiveId(id), []);
  const resetToAll = () => setActiveId('');

  const filtered = useMemo(() => calculatedFilters, [calculatedFilters]);

  const handleApply = () => {
    onApply?.(isAllActive ? [] : [activeId]);
    setOpen(false);
    onClose?.();
  };

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex flex-col items-center justify-start gap-4',
        className,
      )}
      data-component="SaveSectionFilters"
    >
      {/* Top Row: search + filter toggle (mirroring Figma with two control groups) */}
      <div className="flex w-56 items-center justify-between gap-2">
        <div className="flex w-48 items-center gap-2">
          <TextField
            className="w-full max-w-full"
            classNameInputParent="w-full max-w-full"
            classNameInput="h-9 p-2 text-xs font-medium"
            text=""
            placeholderText="Search section..."
            inputValue={searchValue}
            icon={IconSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') setSearchValue('');
            }}
          />
        </div>
        <div className="flex items-center gap-1">
          {/* DropdownMenu*/}
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild className="p-0 focus-within:outline-none">
              <IconSimpleButton
                size={24}
                icon={IconFilter}
                selectedIcon={IconFilterFilled}
                state={open ? 'selected' : 'default'}
                className={clsx('transition-colors duration-200', open && 'bg-neutral-100')}
                aria-label={open ? 'Hide filters' : 'Show filters'}
              />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="start"
              className="bg-neutral-0 z-100000 mt-1 w-full rounded-md border-none p-0 shadow-xl"
              style={{ minWidth: 216 }}
            >
              <div className="flex h-8 items-center border-b border-neutral-100 p-2">
                <span className="text-button-sm leading-tight font-semibold text-neutral-900">
                  Filters
                </span>
              </div>
              <div className="max-h-60 overflow-y-auto px-1">
                <Radio
                  className="m-0 flex flex-col gap-0"
                  classNameRadioItem="m-0 p-2"
                  classNameRadioGroup="gap-0 px-2"
                  value={radioValue}
                  onValueChange={(val: string) =>
                    val === 'ALL' ? resetToAll() : selectFilter(val)
                  }
                  options={[
                    {
                      id: 'filter-all',
                      value: 'ALL',
                      label: (
                        <div className="flex w-full items-center justify-between gap-2 p-2">
                          <span className="text-button-sm flex-1 truncate leading-none font-medium text-neutral-500">
                            {allLabel}
                          </span>
                          <div className="flex h-4 w-6 items-center justify-center rounded-full bg-indigo-100">
                            <span className="text-button-xs leading-none font-medium text-indigo-900">
                              {allCount}
                            </span>
                          </div>
                        </div>
                      ),
                    },
                    ...filtered.map((filterItem) => ({
                      id: `filter-${filterItem.id}`,
                      value: filterItem.id,
                      label: (
                        <div className="flex w-full items-center justify-between gap-2 p-2">
                          <span className="text-button-sm flex-1 truncate leading-none font-medium text-neutral-500">
                            {filterItem.label}
                          </span>
                          <div className="flex h-4 w-6 items-center justify-center rounded-full bg-neutral-100">
                            <span className="text-button-xs leading-none font-normal text-neutral-900">
                              {filterItem.count}
                            </span>
                          </div>
                        </div>
                      ),
                    })),
                  ]}
                />
              </div>
              <div className="flex w-full gap-2 px-2 pt-1 pb-3">
                <PrimaryButton
                  className="h-7 w-full"
                  size="sm"
                  text="Done"
                  icon={null}
                  state="default"
                  onClick={handleApply}
                />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export type { SectionFilterItem };
